import { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { basicFields } from '@bridebook/toolbox/src/google/maps';
import type { Slug } from '@bridebook/toolbox/src/types';
import { AutocompletePrediction, ILatLongBounds } from '@bridebook/web/app-shared/lib/search/types';
import { CustomPredictionService } from '@bridebook/web/app-shared/lib/search/utils/custom-autocomplete-predictions/custom-prediction-service';
import { GooglePlacesService } from '@bridebook/web/app-shared/lib/search/utils/google-places-service';
import { Locales } from '@bridebook/web/lib/i18n/constants';

interface IGetPlaceAutocompleteOptions {
  types: ('geocode' | 'establishment' | 'administrative_area_level_2')[];
  bounds: ILatLongBounds;
  countryCode?: CountryCodes;
}

export interface IGetAutocompletePredictionsOptions
  extends Omit<IGetPlaceAutocompleteOptions, 'types'> {
  category: Slug | '';
  types?: IGetPlaceAutocompleteOptions['types'];
}

export class AutocompletePredictionsService {
  constructor(
    private customPredictionService: CustomPredictionService,
    private googlePlacesService: GooglePlacesService,
  ) {}

  getAutocompletePredictions = async (
    input: string,
    { category, countryCode, bounds, types = ['geocode'] }: IGetAutocompletePredictionsOptions,
  ) => {
    const isNumeric = /\d/.test(input);
    const isFrance = countryCode === CountryCodes.FR;

    if (isNumeric && isFrance) {
      const franceNumericResult = await this.getFranceNumericPredictions(input, bounds);

      if (franceNumericResult) {
        return {
          category,
          predictions: franceNumericResult.combinedPredictions,
          input,
          googlePredictions: franceNumericResult.googlePredictionsMap,
        };
      }
    }

    const googlePredictions = await this.googlePlacesService.getPredictions(
      input,
      this.getPlaceAutocompleteOptions({ types, bounds, countryCode }),
    );

    const predictions = this.getPredictionsWithCustoms(input, countryCode, googlePredictions);

    return {
      category,
      predictions,
      input,
      googlePredictions: googlePredictions.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.place_id]: curr,
        }),
        {},
      ),
    };
  };

  /**
   * Get predictions for France numeric search
   * French couples prefer searching by department, but Bridebook does not support department code searches.
   * Current region-based setup (13 regions) limits monetization. That's why we need extra logic for France to handle department (113 departments) searches.
   * Departments are numeric for example 71, 72, 73, etc. So as long as its is numeric, we can use this logic otherwise we fallback to the default logic.
   */
  private getFranceNumericPredictions = async (input: string, bounds: ILatLongBounds) => {
    const googlePredictions = await this.googlePlacesService.getPredictions(
      input,
      this.getPlaceAutocompleteOptions({
        types: ['administrative_area_level_2'],
        bounds,
        countryCode: CountryCodes.FR,
      }),
    );

    // If no admin area predictions found, return and continue with the default logic
    if (!googlePredictions?.length) {
      return null;
    }

    const place = await this.googlePlacesService.getPlaceDetails(googlePredictions);

    if (place?.geometry?.location) {
      const geocodePredictions = await this.googlePlacesService.getPredictions(
        input,
        this.getPlaceAutocompleteOptions({
          types: ['geocode'],
          bounds: {
            sw: { lat: place.geometry.location.lat(), lon: place.geometry.location.lng() },
            ne: { lat: place.geometry.location.lat(), lon: place.geometry.location.lng() },
          },
          countryCode: CountryCodes.FR,
        }),
      );

      const combined = [...googlePredictions, ...geocodePredictions];
      const combinedPredictions = this.getPredictionsWithCustoms(input, CountryCodes.FR, combined);
      const googlePredictionsMap = combined.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.place_id]: curr,
        }),
        {},
      );

      return {
        combinedPredictions,
        googlePredictionsMap,
      };
    }

    const predictions = this.getPredictionsWithCustoms(input, CountryCodes.FR, googlePredictions);
    const googlePredictionsMap = googlePredictions.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.place_id]: curr,
      }),
      {},
    );

    return {
      combinedPredictions: predictions,
      googlePredictionsMap,
    };
  };

  private getPredictionsWithCustoms = (
    input: string,
    countryCode: string | undefined,
    googlePredictions: google.maps.places.AutocompletePrediction[] | null,
  ): AutocompletePrediction[] => {
    const normalizedInput = input.trim().toLocaleLowerCase();
    if (!normalizedInput.length) {
      return [];
    }

    // Add customized predictions
    const customPredictions = this.customPredictionService.getMatching(
      countryCode,
      normalizedInput,
    );
    const googlePredictionsDeduplicated = (googlePredictions || []).filter((prediction) => {
      const exists = customPredictions.find((customPrediction) => {
        const mainTextMatch =
          prediction.structured_formatting.main_text === customPrediction.mainText;
        if (customPrediction.replaceByMainText) {
          return mainTextMatch;
        }
        const secondaryTextMatch =
          prediction.structured_formatting.secondary_text === customPrediction.secondaryText;
        return mainTextMatch && secondaryTextMatch;
      });
      return !exists;
    });

    const finalPredictions = [...customPredictions, ...googlePredictionsDeduplicated];

    if (finalPredictions.length === 0) {
      return [];
    }
    return finalPredictions;
  };

  private getPlaceAutocompleteOptions = ({
    countryCode,
    types,
    bounds = {
      ne: { lat: -90, lon: 180 },
      sw: { lat: 90, lon: 180 },
    },
  }: IGetPlaceAutocompleteOptions): google.maps.places.AutocompleteOptions => {
    const result: google.maps.places.AutocompleteOptions = {
      bounds: new google.maps.LatLngBounds(
        new google.maps.LatLng(bounds.sw.lat, bounds.sw.lon),
        new google.maps.LatLng(bounds.ne.lat, bounds.ne.lon),
      ),
      fields: basicFields,
      types,
    };

    const showOnlyUK = countryCode === CountryCodes.GB;
    const placesCountry = showOnlyUK ? Locales.UK : countryCode;

    if (
      types.includes('geocode') ||
      types.includes('establishment') ||
      types.includes('administrative_area_level_2')
    ) {
      result.componentRestrictions = placesCountry ? { country: placesCountry } : undefined;
    }

    return result;
  };
}
